import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Buildings, Plus, PencilSimple, Archive, ArrowCounterClockwise, Users } from "@phosphor-icons/react";

export default function PlatformAdminPage() {
  const { isPlatformAdmin } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [name, setName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");

  const load = useCallback(() => {
    api.get("/platform/organisations?include_archived=true").then((r) => setOrgs(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!name.trim()) return;
    try { await api.post("/platform/organisations", { name: name.trim() }); setName(""); load(); toast.success("Organisation created"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const rename = async (id) => {
    if (!editName.trim()) return;
    try { await api.put(`/platform/organisations/${id}`, { name: editName.trim() }); setEditingId(null); load(); toast.success("Renamed"); }
    catch (e) { toast.error("Failed"); }
  };
  const archive = async (id) => {
    if (!window.confirm("Archive this organisation? Its users lose access until restored.")) return;
    try { await api.post(`/platform/organisations/${id}/archive`); load(); toast.success("Archived"); }
    catch (e) { toast.error("Failed"); }
  };
  const restore = async (id) => { try { await api.post(`/platform/organisations/${id}/restore`); load(); toast.success("Restored"); } catch (e) { toast.error("Failed"); } };

  if (!isPlatformAdmin) return <div className="text-slate-500">Platform admin only.</div>;

  const active = orgs.filter((o) => !o.archived_at);
  const archived = orgs.filter((o) => o.archived_at);

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="font-display text-3xl font-black tracking-tight text-slate-900 flex items-center gap-2"><Buildings size={28} weight="bold" /> Platform Admin</h1>
        <p className="font-body text-slate-600 text-sm">Manage organisations (companies) on the platform. Each organisation's data, users and configuration are fully isolated.</p>
      </div>

      <div className="bg-white border-2 border-slate-200 rounded-lg p-4 max-w-2xl">
        <h3 className="font-body font-semibold text-slate-800 mb-2">Organisations</h3>
        <div className="flex flex-col gap-2">
          {active.map((o) => (
            <div key={o.id} data-testid={`org-row-${o.id}`} className="flex items-center gap-2 border border-slate-100 rounded px-3 py-2">
              {editingId === o.id ? (
                <>
                  <Input data-testid={`org-edit-${o.id}`} value={editName} onChange={(e) => setEditName(e.target.value)} className="h-9" />
                  <Button data-testid={`org-save-${o.id}`} onClick={() => rename(o.id)} className="h-9 px-3 bg-slate-900 font-semibold text-xs">Save</Button>
                </>
              ) : (
                <>
                  <span className="flex-1 font-semibold text-slate-800">{o.name}</span>
                  <span className="inline-flex items-center gap-1 text-xs text-slate-500"><Users size={14} weight="bold" />{o.member_count}</span>
                  <button data-testid={`org-rename-${o.id}`} onClick={() => { setEditingId(o.id); setEditName(o.name); }} className="text-slate-400 hover:text-slate-700 p-1"><PencilSimple size={16} weight="bold" /></button>
                  <button data-testid={`org-archive-${o.id}`} onClick={() => archive(o.id)} className="text-slate-400 hover:text-red-600 p-1"><Archive size={16} weight="bold" /></button>
                </>
              )}
            </div>
          ))}
          {active.length === 0 && <span className="text-slate-400 text-sm">No organisations.</span>}
        </div>
        <div className="flex gap-2 mt-3 border-t border-slate-100 pt-3">
          <Input data-testid="org-new-name" placeholder="New organisation name" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && create()} className="h-10" />
          <Button data-testid="org-create" onClick={create} className="h-10 px-3 bg-slate-900 font-semibold"><Plus size={16} weight="bold" className="mr-1" />Add</Button>
        </div>

        {archived.length > 0 && (
          <div className="mt-4 border-t border-slate-100 pt-3">
            <div className="text-xs font-bold uppercase text-slate-500 mb-2">Archived</div>
            {archived.map((o) => (
              <div key={o.id} className="flex items-center gap-2 py-1">
                <span className="flex-1 text-slate-500 line-through text-sm">{o.name}</span>
                <button data-testid={`org-restore-${o.id}`} onClick={() => restore(o.id)} className="text-emerald-700 font-bold inline-flex items-center gap-1 text-xs"><ArrowCounterClockwise size={14} weight="bold" />Restore</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
